import type {
  AchievementCategory,
  AchievementId,
  ArenaGameAvailability,
  ArenaGameId,
  DailyTaskCode,
  DiamondSkinId,
  PremiumOfferId,
  PremiumOfferKind,
  SeasonTaskCode,
  TaskMetric,
  TonUsdtPrizeCampaign,
  UpgradeCode,
  UpgradeLevel,
} from "../domain/types";

export type UpgradeLevelConfig = {
  readonly level: UpgradeLevel;
  readonly effectValue: string;
  readonly costToReach: string | null;
};

export type UpgradeTrackConfig = {
  readonly code: UpgradeCode;
  readonly effect: "tap" | "passive" | "energy-capacity" | "energy-recovery";
  readonly initialLevel: UpgradeLevel;
  /** Preserved achievement boundary; higher levels are optional, not daily requirements. */
  readonly coreMaxLevel?: UpgradeLevel;
  readonly levels: readonly UpgradeLevelConfig[];
};

export type AchievementConfig = {
  readonly id: AchievementId;
  readonly category: AchievementCategory;
  readonly metric:
    | "taps"
    | "mechanical-level"
    | "precision-level"
    | "energy-capacity-level"
    | "workshop-stage"
    | "passive-produced"
    | "arena-matches"
    | "arena-wins"
    | "friends"
    | "daily-streak"
    | "season-chains-completed"
    | "maxed-upgrade-branches"
    | "pvp-wins"
    | "active-friends"
    | "season-rank"
    | "season-percentile";
  readonly target: string;
  readonly comparison?: "at-least" | "at-most";
  readonly finalSeasonOnly?: boolean;
};

export type ArenaCatalogEntry = {
  readonly id: ArenaGameId;
  readonly availability: ArenaGameAvailability;
  readonly art: "penalty" | "resonance" | "relic";
};

export type TaskDefinition<Code extends string> = {
  readonly code: Code;
  readonly metric: TaskMetric;
  readonly target: string;
  readonly reward: string;
  readonly contentKey?: string;
  readonly compatibilityGroup?: string | null;
  readonly audience?: TaskAudienceConfig | null;
  readonly enabled: boolean;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly retroactive: boolean;
  readonly claimPhase?: "immediate" | "season-end";
  readonly chainId?: string | null;
  readonly tier?: number | null;
  readonly prerequisiteCode?: Code | null;
};

export type TaskAudienceConfig = {
  readonly minSeasonDay?: number;
  readonly maxSeasonDay?: number;
  readonly minConfirmedTaps?: string;
  readonly minPrimaryProduced?: string;
  readonly minPassiveProduced?: string;
  readonly minMechanicalLevel?: number;
  readonly minAvailableBalance?: string;
  readonly minArenaMatches?: number;
  readonly requiresAffordableUpgrade?: boolean;
  readonly minAffordableUpgradePurchases?: number;
  readonly requiresCommunityTarget?: "channel" | "chat";
  readonly excludeIfCommunityMember?: "channel" | "chat";
};

export type PremiumOfferConfig = {
  readonly id: PremiumOfferId;
  readonly kind: PremiumOfferKind;
  readonly priceStars: number;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly purchaseLimit: number | null;
  readonly offlineReserveSeconds?: number;
  readonly durationSeconds?: number;
  readonly multiplierBasisPoints?: number;
  readonly grantBasisPoints?: number;
  readonly availableForSeasonSeconds?: number;
  readonly collectibleId?: DiamondSkinId | "season-01-signet";
  readonly featured: boolean;
};

export type GameConfig = {
  readonly version: string;
  readonly economy: {
    readonly initialBalance: string;
    readonly tapReward: string;
    readonly mechanicalCutterCost: string;
    readonly mechanicalCutterPassivePerHour: string;
    readonly passiveRatePeriodSeconds: number;
    readonly offlineCapSeconds: number;
    readonly maxTapBatchSize: number;
    readonly maxAcceptedTapsPerSecond: number;
    readonly maxTapBurstSize: number;
  };
  readonly energy: {
    readonly costPerTap: number;
    readonly dailyRefillLimit: number;
    readonly refillPriceBasisPoints: number;
  };
  readonly balanceModel: {
    readonly seasonDays: number;
    readonly assumedTapsPerSecond: number;
    readonly targetDailyMinutes: number;
    readonly targetPassiveHours: number;
    readonly maxPaybackRemainingBasisPoints: number;
  };
  readonly workshop: {
    readonly visualStageUpgradeThresholds: readonly [number, number, number, number];
  };
  readonly mock: {
    readonly commandLatencyMs: number;
    readonly pvpResolutionLatencyMs: number;
    readonly tapBatchWindowMs: number;
    readonly tapRateLimitRetryMaxAttempts: number;
    readonly tapRateLimitRetryFloorMs: number;
  };
  readonly season: {
    readonly initialRemainingSeconds: number;
    readonly prizeFundUsdCents: string;
    readonly plannedLaunchPriceUsdtMicrosPerToken: string;
    readonly claimPriceStars: number;
    readonly mockOtherEligibleBalance: string;
    readonly mockParticipantCount: number;
    readonly mockReferenceRank: number;
    readonly mockReferenceBalance: string;
    readonly title: string;
    readonly tonUsdtPrize: TonUsdtPrizeCampaign;
  };
  readonly community: {
    readonly channelUrl: string;
    readonly chatUrl: string;
    readonly priorityDailyTaskCodes: readonly [DailyTaskCode, DailyTaskCode];
    readonly finalBalanceBonusBasisPoints: number;
    readonly finalCheckGraceSeconds: number;
  };
  readonly onboarding: {
    readonly version: 4 | 5 | 6;
    readonly tapTarget: number;
    readonly passiveTarget: string;
    readonly firstOutputReward: string;
  };
  readonly arenaCatalog: readonly ArenaCatalogEntry[];
  readonly progress: {
    readonly achievements: readonly AchievementConfig[];
  };
  readonly liveOps: {
    readonly daily: {
      readonly assignmentCount: 3;
      readonly forcedTaskCode: DailyTaskCode | null;
      readonly rewardBase: string;
      readonly rewardDailyStep: string;
      readonly rewardGrowthCapDay: number;
      readonly tasks: readonly TaskDefinition<DailyTaskCode>[];
      readonly streakMilestones: readonly { readonly day: number; readonly bonus: string }[];
    };
    readonly seasonTasks: readonly TaskDefinition<SeasonTaskCode>[];
  };
  readonly premium: {
    readonly starsReference: {
      readonly developerRewardUsdMicrosPerStar: string;
      readonly withdrawalMinimumStars: number;
      readonly checkedAt: string;
    };
    readonly offers: readonly PremiumOfferConfig[];
  };
  readonly rewards: {
    readonly campaignId: string;
    readonly seasonCount: number;
    readonly claimWindowSeconds: number;
    readonly participationCollectibleId: "season-zero-participant";
    readonly claimPricing: {
      readonly lowRewardThresholdUsdtMicros: string;
      readonly lowRewardPriceStars: number;
      readonly standardPriceStars: number;
    };
  };
  readonly retention: {
    readonly quietHoursUtc: readonly [number, number];
    readonly maxMessagesPerUtcDay: number;
    readonly globalCooldownSeconds: number;
    readonly inactiveAfterSeconds: readonly [number, number];
    readonly claimReminderSeconds: readonly [number, number];
    readonly largeUnclaimedRewardUsdtMicros: string;
    readonly dailyRouteReminderSecondsBeforeReset: number;
    readonly seasonEndingReminderSeconds: readonly [number, number];
    readonly qualifyingReturnSeconds: number;
    readonly offlineCollectionMinimumSeconds: number;
  };
  readonly upgrades: Readonly<Record<UpgradeCode, UpgradeTrackConfig>>;
  readonly referrals: {
    readonly levelOneRateBasisPoints: number;
    readonly levelTwoRateBasisPoints: number;
    readonly activationPrimaryThreshold: string;
    readonly confirmedRegistrationReward: string;
    readonly nextMilestoneMembers: number;
    readonly rosterPageSize: number;
  };
  readonly profile: {
    readonly userSeed: string;
    readonly displayName: string;
    readonly seasonsPlayed: number;
    readonly bestPlace: number | null;
    readonly selectedTitle: AchievementId;
  };
  readonly pvp: {
    readonly stake: string;
    readonly zoneCount: number;
    readonly regulationSeries: number;
    readonly maxSuddenDeathSeries: number;
    readonly matchmakingMs: number;
    readonly manualGuardianOfferMs: number;
    readonly introMs: number;
    readonly turnMs: number;
    readonly opponentThinkMinMs: number;
    readonly opponentThinkMaxMs: number;
    readonly revealMs: number;
    readonly guardianName: string;
    readonly guardianInitialBalance: string;
    readonly deterministicSeed: number;
  };
  readonly relicDuel: {
    readonly stakes: readonly [string, string, string, string];
    readonly impulseValues: readonly [1, 2, 3, 4, 5];
    readonly relicValues: readonly [1, 2, 3, 4, 5];
    readonly introMs: number;
    readonly turnMs: number;
    readonly revealMs: number;
    readonly deterministicSeed: number;
  };
  readonly friends: readonly {
    readonly id: string;
    readonly name: string;
    readonly line: 1 | 2;
    readonly primaryProduced: string;
  }[];
  readonly devFixtures: {
    readonly upgradedBalance: string;
    readonly insufficientBalance: string;
    readonly networkBalance: string;
    readonly partialBalance: string;
    readonly partialConfirmedTaps: number;
    readonly offlineStartingBalance: string;
    readonly offlineSeconds: number;
    readonly frozenBalance: string;
    readonly rewardBalance: string;
    readonly arenaReadyBalance: string;
    readonly largeBalance: string;
    readonly activePvpAvailable: string;
    readonly activePvpReserved: string;
    readonly activePvpStake: string;
    readonly energyEmptyBalance: string;
  };
  readonly motion: {
    readonly tapContactMs: number;
    readonly tapStrikeMs: number;
    readonly tapReactionMs: number;
    readonly tapRewardMs: number;
    readonly balanceMs: number;
    readonly upgradeMs: number;
    readonly navigationMs: number;
    readonly onboardingScrollMs: number;
  };
};

export const GAME_CONFIG: GameConfig = {
  version: "season-one-workshop-expansion-2026-09-09",
  economy: {
    initialBalance: "0",
    tapReward: "10",
    mechanicalCutterCost: "100",
    mechanicalCutterPassivePerHour: "540",
    passiveRatePeriodSeconds: 3600,
    offlineCapSeconds: 4 * 60 * 60,
    maxTapBatchSize: 48,
    maxAcceptedTapsPerSecond: 12,
    maxTapBurstSize: 48,
  },
  energy: {
    costPerTap: 1,
    dailyRefillLimit: 2,
    refillPriceBasisPoints: 8_000,
  },
  balanceModel: {
    seasonDays: 30,
    assumedTapsPerSecond: 4,
    targetDailyMinutes: 15,
    targetPassiveHours: 8,
    maxPaybackRemainingBasisPoints: 8_500,
  },
  workshop: {
    visualStageUpgradeThresholds: [0, 1, 7, 14],
  },
  mock: {
    commandLatencyMs: 90,
    pvpResolutionLatencyMs: 680,
    tapBatchWindowMs: 800,
    tapRateLimitRetryMaxAttempts: 5,
    tapRateLimitRetryFloorMs: 120,
  },
  season: {
    initialRemainingSeconds: 30 * 24 * 60 * 60,
    prizeFundUsdCents: "30000000",
    plannedLaunchPriceUsdtMicrosPerToken: "10000",
    claimPriceStars: 160,
    mockOtherEligibleBalance: "0",
    mockParticipantCount: 1,
    mockReferenceRank: 1,
    mockReferenceBalance: "1280",
    title: "Сезон 1 — Восхождение граней",
    tonUsdtPrize: {
      campaignId: "season-one-ton-usdt-leaders-v1",
      seasonCode: "diamond-kingdom-season-1-v1",
      asset: "USDT",
      network: "ton-mainnet",
      totalUsdtMicros: "500000000",
      tiers: [
        { place: 1, amountUsdtMicros: "200000000" },
        { place: 2, amountUsdtMicros: "100000000" },
        { place: 3, amountUsdtMicros: "50000000" },
        { place: 4, amountUsdtMicros: "50000000" },
        { place: 5, amountUsdtMicros: "50000000" },
        { place: 6, amountUsdtMicros: "50000000" },
      ],
      tiePolicy: "split-occupied-prizes",
      claimWindowSeconds: null,
      fundingStatus: "reserved",
    },
  },
  community: {
    channelUrl: "https://t.me/diamondkingdom",
    chatUrl: "https://t.me/+IIa-BncbF6wwOWEy",
    priorityDailyTaskCodes: ["join-channel", "join-chat"],
    finalBalanceBonusBasisPoints: 5_000,
    finalCheckGraceSeconds: 30 * 60,
  },
  onboarding: {
    version: 6,
    tapTarget: 10,
    passiveTarget: "100",
    firstOutputReward: "100",
  },
  arenaCatalog: [
    { id: "crystal-penalty", availability: "playable", art: "penalty" },
    { id: "resonance-rush", availability: "coming-soon", art: "resonance" },
    { id: "relic-duel", availability: "coming-soon", art: "relic" },
  ],
  progress: {
    achievements: [
      { id: "first-spark", category: "craft", metric: "taps", target: "10" },
      { id: "living-facet", category: "craft", metric: "mechanical-level", target: "2" },
      { id: "master-rhythm", category: "season", metric: "taps", target: "100" },
      { id: "light-architect", category: "season", metric: "workshop-stage", target: "3" },
      { id: "makers-circle", category: "friends", metric: "friends", target: "3" },
      { id: "perfect-facet", category: "craft", metric: "precision-level", target: "5" },
      { id: "crystal-reserve", category: "craft", metric: "energy-capacity-level", target: "3" },
      { id: "silent-current", category: "season", metric: "passive-produced", target: "100000" },
      { id: "arena-guardian", category: "season", metric: "arena-wins", target: "10" },
      { id: "season-one-participant", category: "season", metric: "taps", target: "1", finalSeasonOnly: true },
      { id: "streak-seven", category: "season", metric: "daily-streak", target: "7" },
      { id: "streak-fifteen", category: "season", metric: "daily-streak", target: "15" },
      { id: "streak-thirty", category: "season", metric: "daily-streak", target: "30" },
      { id: "chain-conqueror", category: "season", metric: "season-chains-completed", target: "6" },
      { id: "branch-master", category: "craft", metric: "maxed-upgrade-branches", target: "1" },
      { id: "workshop-sovereign", category: "craft", metric: "maxed-upgrade-branches", target: "4" },
      { id: "pvp-first-win", category: "season", metric: "arena-wins", target: "1" },
      { id: "pvp-ten-wins", category: "season", metric: "arena-wins", target: "10" },
      { id: "arena-ten-matches", category: "season", metric: "arena-matches", target: "10" },
      { id: "arena-fifty-matches", category: "season", metric: "arena-matches", target: "50" },
      { id: "active-friend-one", category: "friends", metric: "active-friends", target: "1" },
      { id: "active-friend-three", category: "friends", metric: "active-friends", target: "3" },
      { id: "active-friend-ten", category: "friends", metric: "active-friends", target: "10" },
      { id: "top-ten-percent", category: "season", metric: "season-percentile", target: "10", comparison: "at-most", finalSeasonOnly: true },
      { id: "top-hundred", category: "season", metric: "season-rank", target: "100", comparison: "at-most", finalSeasonOnly: true },
      { id: "top-ten", category: "season", metric: "season-rank", target: "10", comparison: "at-most", finalSeasonOnly: true },
      { id: "season-champion", category: "season", metric: "season-rank", target: "1", comparison: "at-most", finalSeasonOnly: true },
    ],
  },
  liveOps: {
    daily: {
      assignmentCount: 3,
      forcedTaskCode: null,
      rewardBase: "6000",
      rewardDailyStep: "500",
      rewardGrowthCapDay: 30,
      tasks: [
        { code: "tap-light-touch", contentKey: "tap-light-touch", compatibilityGroup: "rhythm", metric: "confirmed-taps", target: "180", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "tap-steady-hand", contentKey: "tap-steady-hand", compatibilityGroup: "rhythm", metric: "confirmed-taps", target: "300", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "tap-precision", contentKey: "tap-precision", compatibilityGroup: "rhythm", audience: { minConfirmedTaps: "500" }, metric: "confirmed-taps", target: "450", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "tap-clear-facet", contentKey: "tap-clear-facet", compatibilityGroup: "rhythm", audience: { minConfirmedTaps: "2000" }, metric: "confirmed-taps", target: "600", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "tap-master-tempo", contentKey: "tap-master-tempo", compatibilityGroup: "rhythm", audience: { minConfirmedTaps: "6000" }, metric: "confirmed-taps", target: "900", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "tap-long-cut", contentKey: "tap-long-cut", compatibilityGroup: "rhythm", audience: { minConfirmedTaps: "15000" }, metric: "confirmed-taps", target: "1200", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "energy-deep-cut", contentKey: "energy-deep-cut", compatibilityGroup: "rhythm", audience: { minSeasonDay: 3 }, metric: "energy-spent", target: "350", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "energy-full-cycle", contentKey: "energy-full-cycle", compatibilityGroup: "rhythm", audience: { minSeasonDay: 10 }, metric: "energy-spent", target: "650", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "primary-first-shift", contentKey: "primary-first-shift", compatibilityGroup: "production", metric: "primary-produced", target: "6000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "primary-bright-shift", contentKey: "primary-bright-shift", compatibilityGroup: "production", metric: "primary-produced", target: "10000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "primary-full-shift", contentKey: "primary-full-shift", compatibilityGroup: "production", audience: { minPrimaryProduced: "30000" }, metric: "primary-produced", target: "16000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "primary-strong-shift", contentKey: "primary-strong-shift", compatibilityGroup: "production", audience: { minPrimaryProduced: "100000" }, metric: "primary-produced", target: "24000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "primary-master-shift", contentKey: "primary-master-shift", compatibilityGroup: "production", audience: { minPrimaryProduced: "400000" }, metric: "primary-produced", target: "36000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "primary-grand-order", contentKey: "primary-grand-order", compatibilityGroup: "production", audience: { minPrimaryProduced: "1500000" }, metric: "primary-produced", target: "55000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "passive-quiet-current", contentKey: "passive-quiet-current", compatibilityGroup: "production", audience: { minMechanicalLevel: 1 }, metric: "passive-produced", target: "1200", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "passive-crystal-pulse", contentKey: "passive-crystal-pulse", compatibilityGroup: "production", audience: { minMechanicalLevel: 2 }, metric: "passive-produced", target: "2400", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "passive-light-stream", contentKey: "passive-light-stream", compatibilityGroup: "production", audience: { minMechanicalLevel: 3 }, metric: "passive-produced", target: "5000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "passive-engine-room", contentKey: "passive-engine-room", compatibilityGroup: "production", audience: { minMechanicalLevel: 5 }, metric: "passive-produced", target: "9000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "offline-return", contentKey: "offline-return", compatibilityGroup: "production", audience: { minMechanicalLevel: 1 }, metric: "offline-collections", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "offline-vault", contentKey: "offline-vault", compatibilityGroup: "production", audience: { minMechanicalLevel: 3, minSeasonDay: 7 }, metric: "offline-collections", target: "2", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "return-second-session", contentKey: "return-second-session", compatibilityGroup: "venture", metric: "return-visits", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "arena-first-duel", contentKey: "arena-first-duel", compatibilityGroup: "venture", audience: { minAvailableBalance: "1000" }, metric: "arena-matches", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "arena-double-duel", contentKey: "arena-double-duel", compatibilityGroup: "venture", audience: { minAvailableBalance: "3000", minArenaMatches: 2 }, metric: "arena-matches", target: "3", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "arena-first-win", contentKey: "arena-first-win", compatibilityGroup: "venture", audience: { minAvailableBalance: "1000", minArenaMatches: 1 }, metric: "arena-wins", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "arena-match-series", contentKey: "arena-match-series", compatibilityGroup: "venture", audience: { minSeasonDay: 3, minAvailableBalance: "2000" }, metric: "arena-matches", target: "2", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "upgrade-workshop", contentKey: "upgrade-workshop", compatibilityGroup: "venture", audience: { requiresAffordableUpgrade: true }, metric: "upgrade-purchases", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "upgrade-workshop-double", contentKey: "upgrade-workshop-double", compatibilityGroup: "venture", audience: { minSeasonDay: 8, minAffordableUpgradePurchases: 2 }, metric: "upgrade-purchases", target: "2", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "invite-master", contentKey: "invite-master", compatibilityGroup: "venture", metric: "confirmed-invites", target: "1", reward: "0", enabled: false, startsAt: null, endsAt: null, retroactive: false },
        { code: "join-channel", contentKey: "join-channel", compatibilityGroup: "community-channel", audience: { requiresCommunityTarget: "channel", excludeIfCommunityMember: "channel" }, metric: "channel-membership", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
        { code: "join-chat", contentKey: "join-chat", compatibilityGroup: "community-chat", audience: { requiresCommunityTarget: "chat", excludeIfCommunityMember: "chat" }, metric: "chat-membership", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      ],
      streakMilestones: [
        { day: 3, bonus: "5000" },
        { day: 7, bonus: "15000" },
        { day: 15, bonus: "50000" },
        { day: 30, bonus: "150000" },
      ],
    },
    seasonTasks: [
      { code: "season-taps-1", contentKey: "season-taps-1", chainId: "taps", tier: 1, prerequisiteCode: null, metric: "confirmed-taps", target: "5000", reward: "20000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-taps-2", contentKey: "season-taps-2", chainId: "taps", tier: 2, prerequisiteCode: "season-taps-1", metric: "confirmed-taps", target: "25000", reward: "70000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-taps-3", contentKey: "season-taps-3", chainId: "taps", tier: 3, prerequisiteCode: "season-taps-2", metric: "confirmed-taps", target: "100000", reward: "180000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-primary-1", contentKey: "season-primary-1", chainId: "production", tier: 1, prerequisiteCode: null, metric: "primary-produced", target: "250000", reward: "30000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-primary-2", contentKey: "season-primary-2", chainId: "production", tier: 2, prerequisiteCode: "season-primary-1", metric: "primary-produced", target: "2000000", reward: "120000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-primary-3", contentKey: "season-primary-3", chainId: "production", tier: 3, prerequisiteCode: "season-primary-2", metric: "primary-produced", target: "10000000", reward: "300000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-upgrades-1", contentKey: "season-upgrades-1", chainId: "upgrades", tier: 1, prerequisiteCode: null, metric: "upgrade-purchases", target: "4", reward: "30000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-upgrades-2", contentKey: "season-upgrades-2", chainId: "upgrades", tier: 2, prerequisiteCode: "season-upgrades-1", metric: "upgrade-purchases", target: "12", reward: "100000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-upgrades-3", contentKey: "season-upgrades-3", chainId: "upgrades", tier: 3, prerequisiteCode: "season-upgrades-2", metric: "upgrade-purchases", target: "20", reward: "220000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-arena-1", contentKey: "season-arena-1", chainId: "arena", tier: 1, prerequisiteCode: null, metric: "arena-matches", target: "5", reward: "30000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-arena-2", contentKey: "season-arena-2", chainId: "arena", tier: 2, prerequisiteCode: "season-arena-1", metric: "arena-matches", target: "20", reward: "100000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-arena-3", contentKey: "season-arena-3", chainId: "arena", tier: 3, prerequisiteCode: "season-arena-2", metric: "arena-matches", target: "50", reward: "220000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-routes-1", contentKey: "season-routes-1", chainId: "daily-routes", tier: 1, prerequisiteCode: null, metric: "daily-routes-claimed", target: "3", reward: "30000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-routes-2", contentKey: "season-routes-2", chainId: "daily-routes", tier: 2, prerequisiteCode: "season-routes-1", metric: "daily-routes-claimed", target: "10", reward: "90000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-routes-3", contentKey: "season-routes-3", chainId: "daily-routes", tier: 3, prerequisiteCode: "season-routes-2", metric: "daily-routes-claimed", target: "20", reward: "180000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-routes-4", contentKey: "season-routes-4", chainId: "daily-routes", tier: 4, prerequisiteCode: "season-routes-3", metric: "daily-routes-claimed", target: "30", reward: "350000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-friends-1", contentKey: "season-friends-1", chainId: "active-friends", tier: 1, prerequisiteCode: null, metric: "active-invites", target: "1", reward: "30000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-friends-2", contentKey: "season-friends-2", chainId: "active-friends", tier: 2, prerequisiteCode: "season-friends-1", metric: "active-invites", target: "3", reward: "100000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-friends-3", contentKey: "season-friends-3", chainId: "active-friends", tier: 3, prerequisiteCode: "season-friends-2", metric: "active-invites", target: "10", reward: "250000", enabled: true, startsAt: null, endsAt: null, retroactive: true, claimPhase: "immediate" },
      { code: "season-channel", chainId: "community", tier: 1, prerequisiteCode: null, metric: "channel-membership", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false, claimPhase: "season-end" },
      { code: "season-chat", chainId: "community", tier: 2, prerequisiteCode: "season-channel", metric: "chat-membership", target: "1", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false, claimPhase: "season-end" },
    ],
  },
  premium: {
    starsReference: {
      developerRewardUsdMicrosPerStar: "13000",
      withdrawalMinimumStars: 1_000,
      checkedAt: "2026-08-12",
    },
    offers: [
      { id: "season-start-taps-10", kind: "tap-production-grant", priceStars: 100, startsAt: null, endsAt: null, purchaseLimit: null, grantBasisPoints: 1_000, availableForSeasonSeconds: 5 * 24 * 60 * 60, featured: false },
      { id: "season-start-taps-30", kind: "tap-production-grant", priceStars: 200, startsAt: null, endsAt: null, purchaseLimit: null, grantBasisPoints: 3_000, availableForSeasonSeconds: 5 * 24 * 60 * 60, featured: false },
      { id: "season-start-taps-50", kind: "tap-production-grant", priceStars: 300, startsAt: null, endsAt: null, purchaseLimit: null, grantBasisPoints: 5_000, availableForSeasonSeconds: 5 * 24 * 60 * 60, featured: true },
      { id: "offline-reserve-starter-8h", kind: "offline-reserve", priceStars: 50, startsAt: null, endsAt: null, purchaseLimit: 1, offlineReserveSeconds: 8 * 60 * 60, featured: true },
      { id: "offline-reserve-8h", kind: "offline-reserve", priceStars: 80, startsAt: null, endsAt: null, purchaseLimit: null, offlineReserveSeconds: 8 * 60 * 60, featured: false },
      { id: "offline-reserve-12h", kind: "offline-reserve", priceStars: 120, startsAt: null, endsAt: null, purchaseLimit: null, offlineReserveSeconds: 12 * 60 * 60, featured: false },
      { id: "offline-reserve-24h", kind: "offline-reserve", priceStars: 160, startsAt: null, endsAt: null, purchaseLimit: null, offlineReserveSeconds: 24 * 60 * 60, featured: true },
      { id: "passive-boost-20-24h", kind: "passive-boost", priceStars: 160, startsAt: null, endsAt: null, purchaseLimit: null, durationSeconds: 24 * 60 * 60, multiplierBasisPoints: 12_000, featured: true },
      { id: "passive-boost-20-6h", kind: "passive-boost", priceStars: 80, startsAt: null, endsAt: null, purchaseLimit: null, durationSeconds: 6 * 60 * 60, multiplierBasisPoints: 12_000, featured: false },
      { id: "tap-boost-50-30m", kind: "tap-boost", priceStars: 80, startsAt: null, endsAt: null, purchaseLimit: 3, durationSeconds: 30 * 60, multiplierBasisPoints: 15_000, featured: true },
      { id: "skin-aurora-prism", kind: "cosmetic", priceStars: 230, startsAt: null, endsAt: null, purchaseLimit: 1, collectibleId: "skin-aurora-prism", featured: false },
      { id: "skin-solar-heart", kind: "cosmetic", priceStars: 230, startsAt: null, endsAt: null, purchaseLimit: 1, collectibleId: "skin-solar-heart", featured: false },
      { id: "skin-emerald-abyss", kind: "cosmetic", priceStars: 385, startsAt: null, endsAt: null, purchaseLimit: 1, collectibleId: "skin-emerald-abyss", featured: false },
      { id: "skin-black-diamond", kind: "cosmetic", priceStars: 385, startsAt: null, endsAt: null, purchaseLimit: 1, collectibleId: "skin-black-diamond", featured: true },
    ],
  },
  rewards: {
    // Season Zero remains a closed, separate campaign. Seasons 1–6 share this
    // campaign so a late entrant can claim any non-zero season independently.
    campaignId: "diamond-kingdom-seasons-1-6-v1",
    seasonCount: 6,
    claimWindowSeconds: 48 * 60 * 60,
    // Historical permanent collectible kept in the Hall of Fame catalog.
    participationCollectibleId: "season-zero-participant",
    claimPricing: {
      lowRewardThresholdUsdtMicros: "50000000",
      lowRewardPriceStars: 50,
      standardPriceStars: 160,
    },
  },
  retention: {
    quietHoursUtc: [20, 7],
    maxMessagesPerUtcDay: 2,
    globalCooldownSeconds: 8 * 60 * 60,
    inactiveAfterSeconds: [24 * 60 * 60, 72 * 60 * 60],
    claimReminderSeconds: [24 * 60 * 60, 6 * 60 * 60],
    largeUnclaimedRewardUsdtMicros: "100000000",
    dailyRouteReminderSecondsBeforeReset: 2 * 60 * 60,
    seasonEndingReminderSeconds: [24 * 60 * 60, 3 * 60 * 60],
    qualifyingReturnSeconds: 45 * 60,
    offlineCollectionMinimumSeconds: 15 * 60,
  },
  upgrades: {
    precisionCutter: {
      code: "precisionCutter",
      effect: "tap",
      initialLevel: 1,
      coreMaxLevel: 10,
      levels: [
        { level: 1, effectValue: "10", costToReach: null },
        { level: 2, effectValue: "15", costToReach: "2500" },
        { level: 3, effectValue: "21", costToReach: "10000" },
        { level: 4, effectValue: "28", costToReach: "35000" },
        { level: 5, effectValue: "36", costToReach: "60000" },
        { level: 6, effectValue: "45", costToReach: "120000" },
        { level: 7, effectValue: "55", costToReach: "250000" },
        { level: 8, effectValue: "85", costToReach: "550000" },
        { level: 9, effectValue: "100", costToReach: "600000" },
        { level: 10, effectValue: "120", costToReach: "800000" },
        { level: 11, effectValue: "150", costToReach: "2400000" },
        { level: 12, effectValue: "185", costToReach: "4500000" },
        { level: 13, effectValue: "225", costToReach: "8000000" },
        { level: 14, effectValue: "270", costToReach: "14000000" },
      ],
    },
    mechanicalCutter: {
      code: "mechanicalCutter",
      effect: "passive",
      initialLevel: 0,
      coreMaxLevel: 10,
      levels: [
        { level: 0, effectValue: "0", costToReach: null },
        { level: 1, effectValue: "540", costToReach: "100" },
        { level: 2, effectValue: "900", costToReach: "7500" },
        { level: 3, effectValue: "1800", costToReach: "30000" },
        { level: 4, effectValue: "3600", costToReach: "60000" },
        { level: 5, effectValue: "7200", costToReach: "120000" },
        { level: 6, effectValue: "12000", costToReach: "250000" },
        { level: 7, effectValue: "24000", costToReach: "600000" },
        { level: 8, effectValue: "58000", costToReach: "1200000" },
        { level: 9, effectValue: "90000", costToReach: "1300000" },
        { level: 10, effectValue: "135000", costToReach: "1800000" },
        { level: 11, effectValue: "175000", costToReach: "3200000" },
        { level: 12, effectValue: "225000", costToReach: "5800000" },
        { level: 13, effectValue: "285000", costToReach: "10000000" },
        { level: 14, effectValue: "355000", costToReach: "16000000" },
      ],
    },
    energyCapacity: {
      code: "energyCapacity",
      effect: "energy-capacity",
      initialLevel: 0,
      coreMaxLevel: 6,
      levels: [
        { level: 0, effectValue: "750", costToReach: null },
        { level: 1, effectValue: "950", costToReach: "20000" },
        { level: 2, effectValue: "1150", costToReach: "75000" },
        { level: 3, effectValue: "1450", costToReach: "250000" },
        { level: 4, effectValue: "1750", costToReach: "500000" },
        { level: 5, effectValue: "2150", costToReach: "550000" },
        { level: 6, effectValue: "2600", costToReach: "750000" },
        { level: 7, effectValue: "3300", costToReach: "2000000" },
        { level: 8, effectValue: "4200", costToReach: "4500000" },
        { level: 9, effectValue: "5400", costToReach: "8000000" },
      ],
    },
    energyRecovery: {
      code: "energyRecovery",
      effect: "energy-recovery",
      initialLevel: 0,
      coreMaxLevel: 6,
      levels: [
        { level: 0, effectValue: "18", costToReach: null },
        { level: 1, effectValue: "15", costToReach: "30000" },
        { level: 2, effectValue: "12", costToReach: "110000" },
        { level: 3, effectValue: "10", costToReach: "350000" },
        { level: 4, effectValue: "8", costToReach: "650000" },
        { level: 5, effectValue: "7", costToReach: "700000" },
        { level: 6, effectValue: "6", costToReach: "1000000" },
        { level: 7, effectValue: "5", costToReach: "3000000" },
        { level: 8, effectValue: "4", costToReach: "7000000" },
        { level: 9, effectValue: "3", costToReach: "14000000" },
      ],
    },
  },
  referrals: {
    levelOneRateBasisPoints: 1_000,
    levelTwoRateBasisPoints: 300,
    activationPrimaryThreshold: "50000",
    confirmedRegistrationReward: "5000",
    nextMilestoneMembers: 15,
    rosterPageSize: 5,
  },
  profile: {
    userSeed: "mock-user-alex-001",
    displayName: "Алекс",
    seasonsPlayed: 3,
    bestPlace: 42,
    selectedTitle: "living-facet",
  },
  pvp: {
    stake: "1000",
    zoneCount: 3,
    regulationSeries: 3,
    maxSuddenDeathSeries: 3,
    matchmakingMs: 20_000,
    manualGuardianOfferMs: 5_000,
    introMs: 1_500,
    turnMs: 20_000,
    opponentThinkMinMs: 900,
    opponentThinkMaxMs: 1_800,
    revealMs: 2_900,
    guardianName: "Страж Арены",
    guardianInitialBalance: "50000000",
    deterministicSeed: 20260807,
  },
  relicDuel: {
    stakes: ["1000", "10000", "50000", "100000"],
    impulseValues: [1, 2, 3, 4, 5],
    relicValues: [1, 2, 3, 4, 5],
    introMs: 1_050,
    turnMs: 12_000,
    revealMs: 1_850,
    deterministicSeed: 20260904,
  },
  friends: [
    { id: "friend-1", name: "Лея", line: 1, primaryProduced: "268000" },
    { id: "friend-2", name: "Мирон", line: 1, primaryProduced: "184000" },
    { id: "friend-3", name: "Ника", line: 1, primaryProduced: "121000" },
    { id: "friend-4", name: "Тим", line: 1, primaryProduced: "84000" },
    { id: "friend-5", name: "Яна", line: 1, primaryProduced: "52000" },
    { id: "friend-6", name: "Марк", line: 1, primaryProduced: "31000" },
    { id: "friend-7", name: "Вера", line: 1, primaryProduced: "18000" },
    { id: "friend-8", name: "Ася", line: 2, primaryProduced: "170000" },
    { id: "friend-9", name: "Лев", line: 2, primaryProduced: "98000" },
    { id: "friend-10", name: "Элина", line: 2, primaryProduced: "61000" },
    { id: "friend-11", name: "Рин", line: 2, primaryProduced: "34000" },
    { id: "friend-12", name: "Саша", line: 2, primaryProduced: "22000" },
    { id: "friend-13", name: "Кай", line: 2, primaryProduced: "9000" },
  ],
  devFixtures: {
    upgradedBalance: "40",
    insufficientBalance: "90",
    networkBalance: "40",
    partialBalance: "40",
    partialConfirmedTaps: 4,
    offlineStartingBalance: "20",
    offlineSeconds: 90,
    frozenBalance: "280",
    rewardBalance: "1280",
    arenaReadyBalance: "25000",
    largeBalance: "17747554",
    activePvpAvailable: "24000",
    activePvpReserved: "1000",
    activePvpStake: "1000",
    energyEmptyBalance: "20000",
  },
  motion: {
    tapContactMs: 90,
    tapStrikeMs: 140,
    tapReactionMs: 240,
    tapRewardMs: 650,
    balanceMs: 240,
    upgradeMs: 920,
    navigationMs: 220,
    onboardingScrollMs: 760,
  },
};
